/**
 * Anel "Custos por canal" com o uso e a tarifa de cada um.
 *
 * Decisoes nao obvias:
 *  1. A fatia tem a cor do CANAL (vocabulario.ts), a mesma dos eventos e
 *     do detalhe por empresa: voz e serie 1 na pagina inteira.
 *  2. Abaixo do anel vem o "x" de cada tarifa (minutos, conversas,
 *     e-mails) e a propria tarifa. Sem isso o custo e um numero opaco; com
 *     isso, qualquer pessoa confere na calculadora.
 *  3. O selo "Tarifas de exemplo" fica no cabecalho enquanto
 *     `tariffs.source` for 'exemplo'. E a diferenca entre uma previa
 *     honesta e um numero que parece fatura.
 *  4. Sem custo medido (motor real: `costByChannel`/`tariffs` nulos) o cartao
 *     NAO desenha anel nem R$ 0,00: diz que o custo ainda nao e medido e
 *     mostra so o USO, que o motor mede (ligacoes, minutos, mensagens).
 */

import { Badge, DonutChart, Footnote, Icon, SectionCard, type DonutSlice } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { CostByChannel, CostChannel, CostTariffs, PlatformTotals } from '../types';
import { COST_CHANNEL_DOT, COST_CHANNEL_ICON, COST_CHANNEL_LABEL, COST_CHANNEL_ORDER, COST_CHANNEL_TONE } from '../vocabulario';
import { Delta } from './Delta';

function custoDo(custo: CostByChannel, canal: CostChannel): number {
  switch (canal) {
    case 'voice':
      return custo.voiceCents;
    case 'whatsapp':
      return custo.whatsappCents;
    case 'email':
      return custo.emailCents;
    case 'infra':
      return custo.infraCents;
  }
}

/** So o uso, sem tarifa: e o que o motor mede quando ainda nao ha fatura lida. */
function usoMedido(totals: PlatformTotals, canal: CostChannel): string {
  const u = totals.usage;
  switch (canal) {
    case 'voice':
      return `${formatCount(u.voiceCalls)} ligações · ${formatCount(u.voiceMinutes)} min (arredondados por ligação)`;
    case 'whatsapp':
      return `${formatCount(u.whatsappMessages)} mensagens enviadas pela Evolution`;
    case 'email':
      return 'sem canal de e-mail no motor';
    case 'infra':
      return 'servidor e banco ainda sem fatura lida';
  }
}

/** "N ligações · M min cobrados · R$ 1,20/min": o uso e a tarifa, para conferir na calculadora. */
function usoDo(totals: PlatformTotals, canal: CostChannel, tarifas: CostTariffs): string {
  const u = totals.usage;
  switch (canal) {
    case 'voice':
      return `${formatCount(u.voiceCalls)} ligações · ${formatCount(u.voiceMinutes)} min cobrados · ${formatBRLFromCents(tarifas.voicePerMinuteCents)}/min`;
    case 'whatsapp':
      return `${u.whatsappConversations === null ? 'conversas não medidas' : `${formatCount(u.whatsappConversations)} conversas`} · ${formatCount(u.whatsappMessages)} mensagens · ${formatBRLFromCents(tarifas.whatsappOfficialPerConversationCents)}/conversa (Meta) ou ${formatBRLFromCents(
        tarifas.whatsappEvolutionMonthlyPerInstanceCents,
      )}/mês por instância (Evolution)`;
    case 'email':
      return `${formatCount(u.emailsSent)} enviados · ${formatBRLFromCents(tarifas.emailPerThousandCents)}/mil · provedor a definir`;
    case 'infra':
      return `servidor ${formatBRLFromCents(tarifas.infraServerMonthlyCents)} + banco ${formatBRLFromCents(tarifas.infraDatabaseMonthlyCents)} por mês, rateados por volume entre as ativas`;
  }
}

export function CustosPorCanal({
  totals,
  anterior,
  tariffs,
  className = '',
}: {
  readonly totals: PlatformTotals;
  readonly anterior: PlatformTotals | null;
  readonly tariffs: CostTariffs | null;
  readonly className?: string;
}): JSX.Element {
  const custo = totals.costByChannel;
  if (custo === null || tariffs === null || totals.costCents === null) {
    return (
      <SectionCard
        id="custos-por-canal"
        title="Custos por canal"
        icon="raio"
        description="O uso de cada canal no período. O custo em reais entra quando o motor passar a ler as faturas."
        aside={
          <Badge tone="neutral" icon="info">
            Custo não medido
          </Badge>
        }
        className={className}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted">
            O motor ainda não lê as faturas da Vapi, da Evolution, do servidor nem do banco. Nenhum custo é estimado aqui: mostrar R$ 0,00 faria a margem parecer igual à comissão.
          </p>
          <ul className="flex flex-col gap-2 text-xs">
            {COST_CHANNEL_ORDER.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <span aria-hidden="true" className={`mt-1 h-2 w-2 shrink-0 rounded-full ${COST_CHANNEL_DOT[c]}`} />
                <Icon name={COST_CHANNEL_ICON[c]} size="xs" className="mt-0.5 shrink-0 text-ink-muted" />
                <span className="min-w-0 text-ink-muted">
                  <span className="font-medium text-ink">{COST_CHANNEL_LABEL[c]}:</span> {usoMedido(totals, c)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </SectionCard>
    );
  }

  const slices: readonly DonutSlice[] = COST_CHANNEL_ORDER.filter((c) => custoDo(custo, c) > 0).map((c) => ({
    key: c,
    label: COST_CHANNEL_LABEL[c],
    value: custoDo(custo, c),
    tone: COST_CHANNEL_TONE[c],
    display: formatBRLFromCents(custoDo(custo, c)),
  }));

  return (
    <SectionCard
      id="custos-por-canal"
      title="Custos por canal"
      icon="raio"
      description="Quanto a operação custou no período, canal a canal, e o uso que gerou cada valor."
      aside={
        tariffs.source === 'exemplo' ? (
          <Badge tone="warning" icon="info">
            Tarifas de exemplo
          </Badge>
        ) : (
          <Badge tone="good" icon="check">
            Faturas reais
          </Badge>
        )
      }
      className={className}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-ink">{formatBRLFromCents(totals.costCents)}</p>
          <Delta atual={totals.costCents} anterior={anterior === null ? null : anterior.costCents} tipo="dinheiro" sentido="neutro" />
        </div>

        {slices.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtle">Nenhum custo no período: nenhuma empresa ativa consumiu canal nem infra.</p>
        ) : (
          <DonutChart slices={slices} title="Participação de cada canal no custo do período" />
        )}

        <ul className="flex flex-col gap-2 text-xs">
          {COST_CHANNEL_ORDER.map((c) => (
            <li key={c} className="flex items-start gap-2">
              <span aria-hidden="true" className={`mt-1 h-2 w-2 shrink-0 rounded-full ${COST_CHANNEL_DOT[c]}`} />
              <Icon name={COST_CHANNEL_ICON[c]} size="xs" className="mt-0.5 shrink-0 text-ink-muted" />
              <span className="min-w-0 text-ink-muted">
                <span className="font-medium text-ink">{COST_CHANNEL_LABEL[c]}:</span> {usoDo(totals, c, tariffs)}
              </span>
            </li>
          ))}
        </ul>

        <Footnote>
          Tarifas de exemplo, fixas na prévia. Em produção os valores vêm das faturas da Vapi (voz), da Meta ou da Evolution (WhatsApp), do provedor de e-mail (a definir) e da
          infra. Nenhum custo aqui é repassado à empresa: ela vê só o retorno.
        </Footnote>
      </div>
    </SectionCard>
  );
}
